import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import NumberFlow from '@number-flow/react';
import { Calendar } from 'lucide-react';
import { Chip } from './UI';
import { AvatarGroup } from './UserAvatar';
import { Day, TripConfig } from '../types';
import { getRegionsLabel } from '../utils/tripUtils';
import { calendarDate } from '../utils/travelState';

// Same editorial serif used everywhere else in the app (src/index.css --font-serif).
const COUNTDOWN_FONT = '"Cormorant Garamond", serif';

// A rolling-odometer/slot-style countdown via @number-flow/react — a
// maintained, purpose-built number-transition component — rather than the
// split-flap flip-clock library used before, which read as flickery because
// each catch-up step forced a full component remount, interrupting its own
// flip transition instead of animating smoothly through it.
//
// Each digit *position* gets its own independent NumberFlow instance (0–9),
// each sat in its own card — a "shape per digit," count aligned to the
// final day-count's digit length — rather than one instance rendering the
// whole multi-digit number as flowing text. This is deliberately NOT
// NumberFlow's own built-in multi-digit rendering (which draws every digit
// as flowing inline text inside one shared box) — that's what fed the
// single wide "pill" look from the first pass at this. Splitting it
// ourselves is what makes "one shape per digit" possible at all, and each
// shape can then be given its own flip-calendar-style seam line.
//
// Because each digit is now an independent 0–9 counter, NumberFlow's
// `continuous` plugin (which reasons about *cross-digit* position within
// one shared multi-digit instance) has nothing to do here — dropped.
// Instead every instance gets a hardcoded `trend={-1}` ("always animate as
// a decrease"), which is what makes a digit that needs to *borrow* — e.g.
// the ones digit going 0→9 because 50 became 49 — visibly roll DOWN through
// the wrap (0→9 as a −1 step) instead of spinning back UP through 1..9,
// which is what the library's own auto-detected trend would otherwise do
// for that specific transition (since 9 > 0 looks like an increase in
// isolation). Correct because this widget only ever counts down.
// Trip ids whose countdown has already animated during THIS page load —
// module-level (not React state or any Storage API), so it's naturally
// scoped to exactly what "once per session" should mean: reset only by an
// actual page reload, unaffected by which component instance is asking.
// Two things this replaces, in order:
//   1. sessionStorage — its real lifetime doesn't match a user's sense of
//      "a session": an installed iOS PWA very often reuses the same
//      WebView across backgrounding/reopening rather than truly reloading,
//      so sessionStorage silently survived what felt like a fresh app open.
//   2. localStorage + a 30-minute recency window — closer, but introduced
//      its own confusing case: switching between two trips repeatedly
//      while testing (or just using the app) within that window correctly
//      skipped the *second* look at a trip already seen recently, but that
//      reads exactly like "broken" when what you actually did was
//      deliberately switch trips and expect each one to animate again.
// A plain in-memory Set sidesteps both: every distinct trip you view for
// the first time since the page loaded animates, revisiting one you've
// already seen this same page life doesn't, and a genuine reload/reopen
// (this variable ceasing to exist, by definition) always starts fresh.
const countdownShownThisPageLoad = new Set<string>();

export function RollingCountdown({ days, tripId, showCaption = true }: {
  days: number;
  tripId: string;
  showCaption?: boolean;
}) {
  const digitCount = String(days).length; // final value only, so box count never changes mid-roll
  const maxDisplayable = Math.pow(10, digitCount) - 1;

  // Each digit box is sized to nearly fill whatever width the parent (the
  // trip card) gives it, split evenly across `digitCount` boxes plus gaps.
  const containerRef = useRef<HTMLDivElement>(null);
  const [boxWidth, setBoxWidth] = useState(56);
  const GAP = 8;

  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const measure = () => {
      const w = el.clientWidth;
      if (!w) return;
      const totalGap = GAP * Math.max(0, digitCount - 1);
      const perBox = Math.floor((w - totalGap) / digitCount);
      setBoxWidth(Math.min(100, Math.max(32, perBox)));
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [digitCount]);

  // See countdownShownThisPageLoad above for why this is a plain in-memory
  // Set rather than sessionStorage or a localStorage recency window.
  const [sessionSeen] = useState(() => countdownShownThisPageLoad.has(tripId));

  useEffect(() => {
    // Same reasoning as before for the delay: Home mounting right after a
    // trip switch can briefly render with settling props before its real,
    // stable render. If that transient mount unmounts again quickly, the
    // cleanup below cancels this before it ever marks the trip as shown.
    const t = setTimeout(() => {
      countdownShownThisPageLoad.add(tripId);
    }, 500);
    return () => clearTimeout(t);
  }, [tripId]);

  // `null` is a genuine third state here, not just "no value yet" — it means
  // "render the box empty," used only for the days===1 reveal below.
  const [flowValue, setFlowValue] = useState<number | null>(() => {
    // "1 day to go" is a milestone worth its own treatment rather than the
    // generic numeric flourish — which for a final value this small would
    // start from a fairly arbitrary synthetic number anyway. Instead the
    // box starts genuinely blank and reveals "1" once mounted (see the
    // render below and the `animate-fade-up` on its first-ever NumberFlow).
    if (days === 1) return sessionSeen ? 1 : null;

    // Already animated this session → start at the final value so the effect
    // below finds nothing to roll and the number just renders as-is.
    if (sessionSeen) return days;

    let stored: number | null = null;
    try {
      const raw = localStorage.getItem(`talisman-countdown-${tripId}`);
      if (raw !== null) stored = parseInt(raw, 10);
    } catch { /* private mode / storage blocked */ }
    const MIN_FLOURISH_STEPS = 7;
    let wanted = Math.max(stored ?? 0, days + MIN_FLOURISH_STEPS);

    // Guarantee the LEADING digit box also visibly moves, not just the
    // trailing ones. A flat +7 alone often lands in the same "leading digit
    // band" as `days` (e.g. days=12 → start=19: both are 1_, so the tens
    // box never changes and reads as broken/uncoded, even though the ones
    // box is rolling correctly) — bump the start up into the next band
    // when that would otherwise happen.
    const leadPlace = Math.pow(10, digitCount - 1);
    const leadingDigit = (n: number) => Math.floor(n / leadPlace) % 10;
    if (digitCount > 1 && leadingDigit(wanted) === leadingDigit(days)) {
      const nextBandStart = (Math.floor(days / leadPlace) + 1) * leadPlace;
      wanted = Math.max(wanted, nextBandStart);
    }
    return Math.min(wanted, maxDisplayable);
  });

  useEffect(() => {
    try { localStorage.setItem(`talisman-countdown-${tripId}`, String(days)); } catch { /* ignore */ }

    if (flowValue === days) return; // nothing to roll — days===1's blank state handles itself via mount
    const t = setTimeout(() => setFlowValue(days), 150);
    return () => clearTimeout(t);
    // Deliberately omits `flowValue` — this should fire once per mount (or
    // when `days` itself changes, e.g. a midnight rollover while viewing),
    // not re-fire as a reaction to the roll it just started.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days, tripId]);

  // `null` entries render an empty box (the days===1 blank-reveal state);
  // never fed to NumberFlow, which only ever receives a real 0–9 digit.
  const digitChars: (number | null)[] = flowValue === null
    ? Array(digitCount).fill(null)
    : String(flowValue).padStart(digitCount, '0').split('').map(Number);
  const boxHeight = Math.round(boxWidth * 1.15);
  const fontSize = Math.round(boxWidth * 0.56);
  // "A bit slower" per direct feedback on the first pass (was 1400ms) —
  // this is the one knob that controls how long the roll takes, regardless
  // of how many digits move or how far each one spins.
  const ROLL_DURATION = 1900;

  return (
    <div className="flex flex-col items-center gap-4 w-full">
      <div ref={containerRef} className="w-full px-1 flex justify-center gap-2">
        {digitChars.map((ch, i) => (
          <div
            key={i}
            className="relative flex-shrink-0 flex items-center justify-center bg-white rounded-xl border border-app-border/40 shadow-[0_3px_8px_-3px_rgba(58,50,38,0.18)] overflow-hidden"
            style={{ width: boxWidth, height: boxHeight }}
          >
            {ch !== null && (
              <NumberFlow
                value={ch}
                trend={-1}
                transformTiming={{ duration: ROLL_DURATION, easing: 'cubic-bezier(0.22, 1, 0.36, 1)' }}
                spinTiming={{ duration: ROLL_DURATION, easing: 'cubic-bezier(0.34, 1.28, 0.64, 1)' }}
                opacityTiming={{ duration: 350, easing: 'ease-out' }}
                style={{
                  fontFamily: COUNTDOWN_FONT, fontWeight: 600, color: '#3a3226', fontSize,
                  // Cormorant Garamond defaults to oldstyle figures — 1/2 sit
                  // at x-height, 6/8 have ascenders, 3/4/5/7/9 have
                  // descenders — so each digit's own glyph sits at a
                  // different height within a flex-centered box, even
                  // though the box itself is centered correctly. Lining
                  // figures are all cut to the same cap-height with no
                  // ascenders/descenders, so every digit's midpoint aligns.
                  fontVariantNumeric: 'lining-nums tabular-nums',
                }}
                className="animate-fade-up"
              />
            )}
            {/* Flip-calendar seam — where a real flip card's two flaps meet. */}
            <div className="absolute left-1.5 right-1.5 top-1/2 h-px bg-app-border/45 -translate-y-1/2 pointer-events-none" />
          </div>
        ))}
      </div>
      {/* Home's feature card renders this label itself (with its own
          spacing, restored from the pre-switcher design), so it opts out
          here rather than showing it twice. */}
      {showCaption && (
        <div className="text-xs font-bold uppercase tracking-widest text-app-sub/50">
          {days === 1 ? 'Day' : 'Days'} Until Departure
        </div>
      )}
    </div>
  );
}

// Trip name / date range / region / travellers — the shared "who, when,
// where" summary block. Rendered inside TripSwitcherCard's card (Home) and,
// uncontainerised, at the top of the Logistics tab (TripOverview). The
// countdown (RollingCountdown, exported above) is NOT rendered here any
// more — it moved to its own standalone Home section, below the "Your
// Trip" card, so that card's height stays constant across every trip in
// the carousel regardless of mode.
export function TripIdentitySummary({ tripConfig, itineraryDays, showTitle = true, showTravellers = true }: {
  tripConfig: TripConfig;
  itineraryDays: Day[];
  showTitle?: boolean;
  /** Off while an edit/travellers panel is open below this block — the
   *  region pill and date range stay visible for context, but the
   *  travellers row would just duplicate what that panel is showing. */
  showTravellers?: boolean;
}) {
  const regionsLabel = getRegionsLabel(tripConfig, itineraryDays);

  const active = tripConfig.travellers.filter(u => u.inviteStatus !== 'pending');

  // One even rhythm for the whole stack rather than per-element margins.
  // Those had drifted to 12/24/24px with a trailing mb-6 on the last row,
  // which stacked on top of the card's own p-6 padding — ~48px of dead
  // space under the travellers row, and the card reading bottom-heavy.
  // space-y-4 spaces between children only, so there's no trailing margin
  // to fight the padding, and it stays even as rows drop in and out
  // (no region on a single-country trip, no travellers while a panel is up).
  return (
    <div className="space-y-4">
      {showTitle && (
        <div className={`font-serif text-3xl font-bold tracking-tight ${!tripConfig.title ? 'text-app-sub/40' : 'text-app-text'}`}>
          {tripConfig.title || 'New Trip'}
        </div>
      )}
      {/* Region pill sits above the date range (not below it with the
          travellers row, where it used to live) — it reads as part of the
          trip's headline identity rather than part of the people block. */}
      {regionsLabel && (
        <div><Chip label={regionsLabel} color="#c5a059" dot solid /></div>
      )}
      <div className="text-app-sub/60 text-[11px] font-black uppercase tracking-widest flex items-center gap-2">
        <Calendar className="w-3 h-3 text-gold" />
        {calendarDate(tripConfig.startDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })} –{' '}
        {calendarDate(tripConfig.endDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
      </div>
      {showTravellers && (
        <div className="flex items-center gap-3">
          <AvatarGroup users={active} size={24} max={6} twoInitials />
          <span className="text-[13px] font-normal text-app-sub/70">
            {active.length} {active.length === 1 ? 'traveller' : 'travellers'}
          </span>
        </div>
      )}
    </div>
  );
}
